import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Gift, CreditCard, Percent, Shield, Star, ArrowRight, Users, Zap, Menu, X } from 'lucide-react';

const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_c9a69f8c-f09a-4a22-b109-5dc5ad9f5a15/artifacts/qtk9sn35_MEDX-AI-Welcome-Poster.pptx%20%281%29.png";

const BANNERS = [
  {
    img: "https://customer-assets-agu9un31.emergentagent.net/job_c9a69f8c-f09a-4a22-b109-5dc5ad9f5a15/artifacts/o9huwsva_pic%201%20%283%29.png",
    tag: "Rewards",
    title: "Earn While You Spend",
    subtitle: "Get exclusive cashback, loyalty points, and partner discounts every time you transact.",
  },
  {
    img: "https://customer-assets-agu9un31.emergentagent.net/job_c9a69f8c-f09a-4a22-b109-5dc5ad9f5a15/artifacts/r0fotm51_ChatGPT%20Image%20Aug%201%2C%202026%2C%2003_53_16%20PM.png",
    tag: "Digital Banking",
    title: "Banking Made Simple",
    subtitle: "Premium Diamond Card, instant payments, and full financial management at your fingertips.",
  },
  {
    img: "https://customer-assets-agu9un31.emergentagent.net/job_c9a69f8c-f09a-4a22-b109-5dc5ad9f5a15/artifacts/3hkxf89c_pic%202%20%282%29.png",
    tag: "Empowerment",
    title: "Built for the Working Class",
    subtitle: "Access partner rewards, discount vouchers, and premium financial solutions exclusively for you.",
  },
];

const FEATURES = [
  { icon: Gift, title: "Partner Rewards", desc: "Earn points at 25+ partner stores nationwide", color: "bg-amber-50 text-amber-700" },
  { icon: Percent, title: "Discount Vouchers", desc: "Up to 40% off groceries, fuel, airtime & more", color: "bg-emerald-50 text-emerald-700" },
  { icon: CreditCard, title: "Digital Banking", desc: "Diamond Card with instant payments & transfers", color: "bg-sky-50 text-sky-700" },
  { icon: Shield, title: "Insurance Cover", desc: "Funeral, life, medical & vehicle insurance", color: "bg-violet-50 text-violet-700" },
  { icon: Zap, title: "Quick Loans", desc: "Same-day payday loans from R500 to R8,000", color: "bg-orange-50 text-orange-700" },
  { icon: Star, title: "Premium Benefits", desc: "Exclusive access to financial solutions & tools", color: "bg-rose-50 text-rose-700" },
];

const STATS = [
  { value: "208 000+", label: "Members" },
  { value: "25+", label: "Partner Stores" },
  { value: "R2.4M", label: "Rewards Earned" },
  { value: "40%", label: "Max Discount" },
];

export const WelcomePage = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setCurrent(p => (p + 1) % BANNERS.length), 5000);
    return () => clearInterval(timer);
  }, []);

  const prevSlide = () => setCurrent(p => (p - 1 + BANNERS.length) % BANNERS.length);
  const nextSlide = () => setCurrent(p => (p + 1) % BANNERS.length);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const NAV_LINKS = [
    { label: 'Home', to: '/' },
    { label: 'About Us', to: '/about' },
    { label: 'Our Products', to: '/products' },
    { label: 'Contact Us', to: '/contact' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="welcome-page">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-b border-gray-100 z-50">
        <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
            <img src={LOGO_URL} alt="AfriPerx" className="h-10 w-auto" />
          </Link>
          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-6">
            {NAV_LINKS.map(link => (
              <Link key={link.to} to={link.to} className="text-sm text-gray-600 hover:text-afriperx-green font-medium transition-colors">{link.label}</Link>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-2">
            <button onClick={() => navigate('/login')} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors" data-testid="nav-login">Sign In</button>
            <button onClick={() => navigate('/get-started')} className="px-5 py-2 text-sm bg-afriperx-green hover:bg-afriperx-green-dark text-white rounded-full font-medium transition-colors" data-testid="nav-signup">Get Started</button>
          </div>
          {/* Mobile hamburger */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-gray-600" data-testid="mobile-menu-toggle">
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {/* Mobile dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-4 pt-2 space-y-2 animate-fade-in">
            {NAV_LINKS.map(link => (
              <Link key={link.to} to={link.to} onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm text-gray-700 hover:text-afriperx-green font-medium">{link.label}</Link>
            ))}
            <div className="flex gap-2 pt-2 border-t border-gray-100">
              <button onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} className="flex-1 py-2 text-sm text-gray-600 font-medium border border-gray-200 rounded-full">Sign In</button>
              <button onClick={() => { navigate('/get-started'); setMobileMenuOpen(false); }} className="flex-1 py-2 text-sm bg-afriperx-green text-white font-medium rounded-full">Get Started</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Banner Carousel */}
      <section className="pt-16 relative overflow-hidden" data-testid="hero-carousel">
        <div className="relative h-[500px] md:h-[560px]">
          {BANNERS.map((b, i) => (
            <div
              key={i}
              className={`absolute inset-0 transition-all duration-700 ease-in-out ${i === current ? 'opacity-100 translate-x-0' : i < current ? 'opacity-0 -translate-x-full' : 'opacity-0 translate-x-full'}`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/50 to-transparent z-10" />
              <img src={b.img} alt={b.title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="relative z-20 h-full flex items-center">
                <div className="max-w-6xl mx-auto px-6 md:px-12">
                  <div className="max-w-lg">
                    <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-semibold bg-afriperx-gold/90 text-white px-4 py-1.5 rounded-full mb-4">{b.tag}</span>
                    <h1 className="font-syne text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 leading-tight">{b.title}</h1>
                    <p className="text-white/80 text-base md:text-lg mb-6 leading-relaxed">{b.subtitle}</p>
                    <div className="flex gap-3">
                      <button onClick={() => navigate('/get-started')} className="px-6 py-3 bg-afriperx-gold hover:bg-afriperx-gold-dark text-white font-syne font-bold rounded-full transition-colors text-sm flex items-center gap-2" data-testid="hero-apply-btn">
                        Get Started <ArrowRight className="w-4 h-4" />
                      </button>
                      <button onClick={() => navigate('/login')} className="px-6 py-3 bg-white/15 hover:bg-white/25 text-white font-syne font-bold rounded-full transition-colors text-sm backdrop-blur-sm border border-white/20">
                        Sign In
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Arrows */}
          <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 bg-white/20 hover:bg-white/40 backdrop-blur rounded-full flex items-center justify-center text-white transition-colors" data-testid="prev-banner">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 bg-white/20 hover:bg-white/40 backdrop-blur rounded-full flex items-center justify-center text-white transition-colors" data-testid="next-banner">
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2">
            {BANNERS.map((_, i) => (
              <button key={i} onClick={() => setCurrent(i)} className={`h-2 rounded-full transition-all duration-300 ${i === current ? 'w-8 bg-afriperx-gold' : 'w-2 bg-white/50'}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-afriperx-green py-5">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          {STATS.map((s, i) => (
            <div key={i} className="text-center">
              <p className="font-syne text-2xl md:text-3xl font-bold text-white">{s.value}</p>
              <p className="text-white/70 text-xs mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-semibold text-afriperx-green bg-green-50 px-4 py-1.5 rounded-full mb-4">Why AfriPerx</span>
          <h2 className="font-syne text-2xl md:text-3xl font-bold text-gray-900 mb-4">More Than Just Finance</h2>
          <p className="text-afriperx-gold font-semibold text-base md:text-lg max-w-3xl mx-auto leading-relaxed mb-2">
            Get More Than Just Finance – Apply with AfriPerx and Receive Exclusive Discounts, Cashback Rewards, and Access to Premium Financial Solutions.
          </p>
          <p className="text-gray-500 text-sm max-w-2xl mx-auto">
            Our platform empowers the working class to benefit exclusively from partner rewards and discount benefits, putting more money back in your pocket every month.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="pb-14 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <div key={i} className="group bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-lg hover:shadow-afriperx-gold/5 hover:border-afriperx-gold/20 transition-all duration-300 cursor-pointer">
                <div className={`w-11 h-11 rounded-xl ${f.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="font-syne font-bold text-gray-900 text-sm mb-1">{f.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-14 bg-gray-50 px-4">
        <div className="max-w-4xl mx-auto text-center mb-10">
          <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-semibold text-afriperx-gold bg-amber-50 px-4 py-1.5 rounded-full mb-4">How It Works</span>
          <h2 className="font-syne text-2xl md:text-3xl font-bold text-gray-900">Start Earning in 3 Steps</h2>
        </div>
        <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-6">
          {[
            { num: '01', title: 'Apply Online', desc: 'Complete our quick application form in under 60 seconds. Select the services you need.' },
            { num: '02', title: 'Get Approved', desc: 'Our team reviews your application and contacts you within 2-4 business hours.' },
            { num: '03', title: 'Start Earning', desc: 'Access partner rewards, discount vouchers, and all your financial tools in one place.' },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 relative">
              <span className="font-syne text-4xl font-bold text-afriperx-gold/15 absolute top-4 right-4">{s.num}</span>
              <div className="w-10 h-10 rounded-full bg-afriperx-green text-white flex items-center justify-center font-bold text-sm mb-4">{i + 1}</div>
              <h3 className="font-syne font-bold text-gray-900 mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonial / Trust */}
      <section className="py-14 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-afriperx-green to-afriperx-green-dark rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-48 h-48 bg-afriperx-gold/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-afriperx-gold/5 rounded-full blur-3xl" />
            <div className="relative z-10">
              <Users className="w-10 h-10 text-afriperx-gold mx-auto mb-4" />
              <h2 className="font-syne text-2xl md:text-3xl font-bold text-white mb-3">Join 208 000+ Members</h2>
              <p className="text-white/80 text-sm md:text-base max-w-xl mx-auto mb-6 leading-relaxed">
                Thousands of South African workers are already saving more and earning rewards with AfriPerx. Be part of the movement.
              </p>
              <button onClick={() => navigate('/get-started')} className="px-8 py-3.5 bg-afriperx-gold hover:bg-afriperx-gold-dark text-white font-syne font-bold rounded-full transition-colors text-sm inline-flex items-center gap-2" data-testid="cta-apply-btn">
                Get Started <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-10 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-1">
              <img src={LOGO_URL} alt="AfriPerx" className="h-10 w-auto brightness-200 mb-3" />
              <p className="text-gray-400 text-xs leading-relaxed">Rewarding Africa's Workforce. Empowering the working class with exclusive rewards and premium financial solutions.</p>
            </div>
            <div>
              <h4 className="font-syne font-bold text-white text-sm mb-3">Quick Links</h4>
              <div className="space-y-2">
                <Link to="/" className="block text-gray-400 text-xs hover:text-afriperx-gold transition-colors">Home</Link>
                <Link to="/about" className="block text-gray-400 text-xs hover:text-afriperx-gold transition-colors">About Us</Link>
                <Link to="/products" className="block text-gray-400 text-xs hover:text-afriperx-gold transition-colors">Our Products</Link>
                <Link to="/contact" className="block text-gray-400 text-xs hover:text-afriperx-gold transition-colors">Contact Us</Link>
              </div>
            </div>
            <div>
              <h4 className="font-syne font-bold text-white text-sm mb-3">Services</h4>
              <div className="space-y-2">
                <Link to="/get-started" className="block text-gray-400 text-xs hover:text-afriperx-gold transition-colors">Get Started</Link>
                <Link to="/apply" className="block text-gray-400 text-xs hover:text-afriperx-gold transition-colors">Apply Now</Link>
                <Link to="/login" className="block text-gray-400 text-xs hover:text-afriperx-gold transition-colors">Sign In</Link>
              </div>
            </div>
            <div>
              <h4 className="font-syne font-bold text-white text-sm mb-3">Legal</h4>
              <div className="space-y-2">
                <Link to="/terms" className="block text-gray-400 text-xs hover:text-afriperx-gold transition-colors">Terms & Conditions</Link>
                <button onClick={() => navigate('/admin/login')} className="block text-gray-400 text-xs hover:text-afriperx-gold transition-colors">Admin Portal</button>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-6 text-center">
            <p className="text-gray-600 text-xs">&copy; 2026 AfriPerx. All rights reserved. FSP Licensed.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
